/** CharacterScreen — キャラクター(絵筆)選択モーダル。
 *  Reference: docs/CLAUDE.md §3.1 Painterly Joy / §3.3 Skill Honored / §3.2 Cozy not Cute
 *  - 性能差なし(見た目のみ)
 *  - アンロック:ステージ進行 / Color 購入 / 広告視聴(Phase A デモ用)
 *  - document.body に直接マウント(ShopScreen と同パターン)
 *
 *  Callers:
 *    - web/src/main.ts: new CharacterScreen({ ... }); characterScreen.open()
 *    - web/src/ui/screens/HomeScreen.ts: onOpenCharacter callback → open()
 */

import {
  CharacterStore,
  CHARACTER_IDS,
  CHARACTER_META,
  type CharacterId,
  type CharacterMeta,
} from '../../game/CharacterStore';
import { showConfirmDialog } from '../ConfirmDialog';

export interface CharacterScreenOptions {
  readonly characterStore: CharacterStore;
  readonly getColorBalance: () => number;
  readonly addColor: (amount: number) => void;
  readonly onClose: () => void;
  readonly onSelect: (id: CharacterId) => void;
  readonly onWatchAd?: () => Promise<boolean>;
}

/** 各キャラの SVG プレビュー(ドラゴン胴 + 角 + 目 の簡易シルエット) */
function renderCharacterSvg(meta: CharacterMeta): string {
  const body   = `#${meta.bodyColor.toString(16).padStart(6, '0')}`;
  const accent = `#${meta.accentColor.toString(16).padStart(6, '0')}`;
  const ink    = `#${meta.inkColor.toString(16).padStart(6, '0')}`;
  const cream  = `#${meta.creamColor.toString(16).padStart(6, '0')}`;
  return `
    <svg viewBox="0 0 100 100" width="76" height="76" xmlns="http://www.w3.org/2000/svg">
      <ellipse cx="50" cy="68" rx="26" ry="7" fill="rgba(0,0,0,0.15)" />
      <ellipse cx="46" cy="52" rx="30" ry="22" fill="${body}" stroke="${ink}" stroke-width="2"/>
      <ellipse cx="48" cy="50" rx="20" ry="10" fill="${cream}" opacity="0.4"/>
      <ellipse cx="66" cy="42" rx="18" ry="14" fill="${body}" stroke="${ink}" stroke-width="2"/>
      <polygon points="58,28 62,12 68,30" fill="${accent}" stroke="${ink}" stroke-width="1.5"/>
      <polygon points="70,28 78,12 82,32" fill="${accent}" stroke="${ink}" stroke-width="1.5"/>
      <circle cx="70" cy="42" r="6" fill="#fff" stroke="${ink}" stroke-width="1.5"/>
      <circle cx="72" cy="42" r="3" fill="${ink}"/>
      <circle cx="73" cy="41" r="1" fill="#fff"/>
    </svg>
  `;
}

/** ロック情報テキストを生成 */
function unlockLabel(meta: CharacterMeta): string {
  const { type, stageId, colorPrice } = meta.unlockCondition;
  if (type === 'default')     return '';
  if (type === 'stage_clear') return `ステージ ${stageId ?? ''} クリアで解放`;
  if (type === 'color')       return `${(colorPrice ?? 0).toLocaleString('ja-JP')} Color で解放`;
  if (type === 'reward_ad')   return '動画を見て解放';
  return '';
}

export class CharacterScreen {
  private readonly overlay: HTMLDivElement;
  private readonly options: CharacterScreenOptions;
  private isOpen = false;

  constructor(options: CharacterScreenOptions) {
    this.options = options;
    this.overlay = document.createElement('div');
    this.overlay.className = 'character-overlay';
    this.overlay.setAttribute('aria-hidden', 'true');
    this.overlay.setAttribute('role', 'dialog');
    this.overlay.setAttribute('aria-modal', 'true');
    this.overlay.setAttribute('aria-label', 'キャラクター選択');
    this.overlay.innerHTML = this.buildHTML();
    document.body.appendChild(this.overlay);
    this.wireEvents();
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.overlay.classList.add('is-open');
    this.overlay.setAttribute('aria-hidden', 'false');
    this.refreshTiles();
    this.refreshCurrentLabel();
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.classList.remove('is-open');
    this.overlay.setAttribute('aria-hidden', 'true');
  }

  /** 外部からキャラがアンロックされた後に表示を再同期する */
  refresh(): void {
    if (!this.isOpen) return;
    this.refreshTiles();
    this.refreshCurrentLabel();
  }

  // ---------------------------------------------------------------------------
  // Build HTML
  // ---------------------------------------------------------------------------

  private buildHTML(): string {
    const tilesHTML = CHARACTER_IDS.map((id) => this.buildTileHTML(id)).join('');
    return `
      <div class="character-card rm-card">
        <div class="character-header">
          <div class="character-title">キャラクター選択</div>
          <button class="rm-close-btn" type="button" data-character-close aria-label="閉じる">✕</button>
        </div>
        <div class="character-grid" data-character-grid>
          ${tilesHTML}
        </div>
        <div class="character-footer">
          <div class="character-current-label">現在の絵筆</div>
          <div class="character-current-name" data-character-current-name>見習いの絵筆</div>
        </div>
      </div>
    `;
  }

  private buildTileHTML(id: CharacterId): string {
    const store = this.options.characterStore;
    const meta = CHARACTER_META[id];
    const unlocked = store.isUnlocked(id);
    const selected = store.isSelected(id);
    const state = selected ? 'selected' : unlocked ? 'unlocked' : 'locked';
    const label = unlockLabel(meta);
    const lockHtml = !unlocked && label
      ? `<div class="character-lock-info"><span class="lock-icon">🔒</span><span class="lock-text">${label}</span></div>`
      : '';

    // PNG(codex 生成、TOP/ホーム画風)優先、ロード失敗時は SVG プレビューにフォールバック
    const pngPath = `/icons/characters/${id}.png`;
    return `
      <button class="character-tile" data-character-tile="${id}" data-state="${state}" type="button">
        <div class="character-preview">
          <img class="character-preview-img" src="${pngPath}" alt="${meta.name}"
               onload="this.dataset.loaded='true'" onerror="this.dataset.loaded='error'" />
          <div class="character-preview-svg">${renderCharacterSvg(meta)}</div>
        </div>
        <div class="character-name">${meta.name}</div>
        <div class="character-desc">${meta.description}</div>
        ${lockHtml}
      </button>
    `;
  }

  // ---------------------------------------------------------------------------
  // Wire Events
  // ---------------------------------------------------------------------------

  private wireEvents(): void {
    // Backdrop click → close
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
        this.options.onClose();
      }
    });

    // Close button
    this.overlay
      .querySelector<HTMLButtonElement>('[data-character-close]')
      ?.addEventListener('click', () => {
        this.close();
        this.options.onClose();
      });

    // Tile clicks (event delegation — tiles are recreated on refresh)
    this.overlay
      .querySelector<HTMLElement>('[data-character-grid]')
      ?.addEventListener('click', (e) => {
        const tile = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-character-tile]');
        if (!tile) return;
        const id = tile.dataset.characterTile as CharacterId | undefined;
        if (!id || !CHARACTER_IDS.includes(id)) return;
        void this.handleTileClick(id);
      });
  }

  // ---------------------------------------------------------------------------
  // Tile click handler
  // ---------------------------------------------------------------------------

  private async handleTileClick(id: CharacterId): Promise<void> {
    const store = this.options.characterStore;
    const meta = CHARACTER_META[id];

    if (store.isUnlocked(id)) {
      // 解放済み → 選択
      store.select(id);
      this.options.onSelect(id);
      this.refreshTiles();
      this.refreshCurrentLabel();
      return;
    }

    // 未解放 → アンロックフロー
    const { type, colorPrice } = meta.unlockCondition;

    if (type === 'color' && colorPrice !== undefined) {
      const balance = this.options.getColorBalance();
      if (balance < colorPrice) {
        this.showToast(`Color が足りません (${colorPrice.toLocaleString('ja-JP')} 必要)`);
        return;
      }
      const confirmed = await showConfirmDialog({
        title: meta.name,
        message: `Phase A デモ:実購入は発生しません。<br>${meta.name} を ${colorPrice.toLocaleString('ja-JP')} Color で解放しますか?`,
      });
      if (!confirmed) return;
      this.options.addColor(-colorPrice);
      store.unlockByColor(id);
      store.select(id);
      this.options.onSelect(id);
      this.refreshTiles();
      this.refreshCurrentLabel();
      this.showToast(`🖌️ ${meta.name} 解放!`);
      return;
    }

    if (type === 'reward_ad') {
      const { onWatchAd } = this.options;
      if (!onWatchAd) {
        // Ad overlay 未設定 → デモ解放
        store.unlockByAd(id);
        store.select(id);
        this.options.onSelect(id);
        this.refreshTiles();
        this.refreshCurrentLabel();
        this.showToast(`🖌️ ${meta.name} 解放!`);
        return;
      }
      const ok = await onWatchAd();
      if (!ok) return;
      store.unlockByAd(id);
      store.select(id);
      this.options.onSelect(id);
      this.refreshTiles();
      this.refreshCurrentLabel();
      this.showToast(`🖌️ ${meta.name} 解放!`);
      return;
    }

    if (type === 'stage_clear') {
      const label = unlockLabel(meta);
      this.showToast(`🔒 ${label}`);
    }
  }

  // ---------------------------------------------------------------------------
  // Refresh helpers
  // ---------------------------------------------------------------------------

  private refreshTiles(): void {
    const grid = this.overlay.querySelector<HTMLElement>('[data-character-grid]');
    if (!grid) return;
    grid.innerHTML = CHARACTER_IDS.map((id) => this.buildTileHTML(id)).join('');
  }

  private refreshCurrentLabel(): void {
    const el = this.overlay.querySelector<HTMLElement>('[data-character-current-name]');
    if (!el) return;
    el.textContent = CHARACTER_META[this.options.characterStore.getSelected()].name;
  }

  // ---------------------------------------------------------------------------
  // Toast
  // ---------------------------------------------------------------------------

  private showToast(message: string, durationMs = 1800): void {
    let toast = this.overlay.querySelector<HTMLElement>('[data-character-toast]');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'shop-toast';
      toast.setAttribute('data-character-toast', '');
      this.overlay.querySelector('.character-card')?.appendChild(toast);
    }
    toast.textContent = message;
    toast.hidden = false;
    toast.classList.add('is-visible');
    window.clearTimeout((toast as unknown as { _t?: number })._t);
    (toast as unknown as { _t?: number })._t = window.setTimeout(() => {
      if (!toast) return;
      toast.classList.remove('is-visible');
      window.setTimeout(() => { if (toast) toast.hidden = true; }, 200);
    }, durationMs);
  }
}
